import type { CertificateEntry } from "../../../../../../../../../shared/types/certificates.type";
import { matchSocialCasinoGroups } from "../../../../../../../../../shared/constants/social-casino-groups";

/** Digits-only Google Ads customer id for joining dashboard ↔ certificates. */
export function normalizeAccountId(id: string): string {
	return String(id).replace(/\D/g, "");
}

/** Group certificate rows by normalized account_id. */
export function groupCertificatesByAccount(
	certificates: CertificateEntry[] | undefined,
): Map<string, CertificateEntry[]> {
	const map = new Map<string, CertificateEntry[]>();
	for (const cert of certificates ?? []) {
		const key = normalizeAccountId(cert.account_id);
		if (!key) continue;
		const list = map.get(key);
		if (list) list.push(cert);
		else map.set(key, [cert]);
	}
	return map;
}

const isSocialCasino = (cert: CertificateEntry) =>
	(cert.cert_type || "").toLowerCase() === "social_casino";

/** Max social-casino group chips in the Certs column. */
export const VISIBLE_GROUP_CHIPS = 2;

/** Max individual GEO chips in the Certs column. */
export const VISIBLE_GEO_CHIPS = 4;

/**
 * @deprecated Prefer VISIBLE_GROUP_CHIPS / VISIBLE_GEO_CHIPS — kept for older
 * call sites that pass a single overflow budget.
 */
export const VISIBLE_CERT_CHIPS = VISIBLE_GEO_CHIPS;

export type AccountCertChip = {
	key: string;
	label: string;
	tooltip: string;
	pending: boolean;
};

export type AccountCertDisplay = {
	visible: AccountCertChip[];
	more: number;
	moreTooltip: string;
};

function statusLabel(status: string | undefined): string {
	return (status || "").toLowerCase();
}

/** Manually deactivated rows stay in BJ as Rejected — never show in Certs. */
function isRejected(status: string | undefined): boolean {
	return statusLabel(status) === "rejected";
}

function statusPending(status: string | undefined): boolean {
	const s = statusLabel(status);
	return s !== "approved" && s !== "rejected";
}

/** Rows that may appear as Certs chips (Approved + in-progress; not Rejected). */
export function certsForColumnDisplay(
	certs: CertificateEntry[],
): CertificateEntry[] {
	return certs.filter((c) => !isRejected(c.cert_status));
}

function geosPending(
	certs: CertificateEntry[],
	geos: Set<string>,
	pred: (c: CertificateEntry) => boolean,
): boolean {
	return certs.some(
		(c) => pred(c) && geos.has(c.geo) && statusPending(c.cert_status),
	);
}

/** Build Certs-column chips: social-casino groups + remaining geo codes.

	Social-casino rows are matched per `app_bundle_id`, so two apps with
	Group 2 each yield two "Group 2" chips (same as the account card).
*/
export function getAccountCertDisplay(
	certs: CertificateEntry[],
	options: {
		visibleGroupCount?: number;
		visibleGeoCount?: number;
		/** @deprecated single budget for all chips — prefer separate group/geo caps */
		visibleCount?: number;
	} = {},
): AccountCertDisplay {
	const visibleCerts = certsForColumnDisplay(certs);
	if (!visibleCerts.length) {
		return { visible: [], more: 0, moreTooltip: "" };
	}

	const socialCerts = visibleCerts.filter(isSocialCasino);
	const otherCerts = visibleCerts.filter((c) => !isSocialCasino(c));

	const socialByBundle = new Map<string, CertificateEntry[]>();
	for (const cert of socialCerts) {
		const bundle = (cert.app_bundle_id || "").trim() || "_";
		const list = socialByBundle.get(bundle);
		if (list) list.push(cert);
		else socialByBundle.set(bundle, [cert]);
	}

	const groupChips: AccountCertChip[] = [];
	const geoChips: AccountCertChip[] = [];

	const bundleKeys = [...socialByBundle.keys()].sort((a, b) =>
		a.localeCompare(b),
	);
	for (const bundle of bundleKeys) {
		const rows = socialByBundle.get(bundle) ?? [];
		const socialGeos = [...new Set(rows.map((c) => c.geo).filter(Boolean))];
		const { groups, leftover } = matchSocialCasinoGroups(socialGeos);

		for (const group of groups) {
			const geoSet = new Set(group.geos);
			const tipLines =
				bundle === "_"
					? [group.geos.join(" ")]
					: [bundle, group.geos.join(" ")];
			groupChips.push({
				key: `group-${group.id}-${bundle}`,
				label: group.label,
				tooltip: tipLines.join("\n"),
				pending: geosPending(rows, geoSet, isSocialCasino),
			});
		}

		for (const geo of leftover) {
			const geoRows = rows.filter((c) => c.geo === geo);
			geoChips.push({
				key: `social-${bundle}-${geo}`,
				label: geo,
				tooltip: formatCertRowsTooltip(geoRows),
				pending: geoRows.some((c) => statusPending(c.cert_status)),
			});
		}
	}

	// Stable order: Group 1 chips first, then Group 2, then by bundle.
	groupChips.sort((a, b) => a.key.localeCompare(b.key));

	const otherGeos = [
		...new Set(otherCerts.map((c) => c.geo).filter(Boolean)),
	].sort((a, b) => a.localeCompare(b));

	for (const geo of otherGeos) {
		const rows = otherCerts.filter((c) => c.geo === geo);
		geoChips.push({
			key: `geo-${geo}`,
			label: geo,
			tooltip: formatCertRowsTooltip(rows),
			pending: rows.some((c) => statusPending(c.cert_status)),
		});
	}

	// Legacy single budget: truncate the combined list.
	if (options.visibleCount != null) {
		const chips = [...groupChips, ...geoChips];
		if (chips.length <= options.visibleCount) {
			return { visible: chips, more: 0, moreTooltip: "" };
		}
		const hidden = chips.slice(options.visibleCount);
		return {
			visible: chips.slice(0, options.visibleCount),
			more: hidden.length,
			moreTooltip: hidden.map((c) => c.label).join(" "),
		};
	}

	const groupCap = options.visibleGroupCount ?? VISIBLE_GROUP_CHIPS;
	const geoCap = options.visibleGeoCount ?? VISIBLE_GEO_CHIPS;
	const visibleGroups = groupChips.slice(0, groupCap);
	const hiddenGroups = groupChips.slice(groupCap);
	const visibleGeos = geoChips.slice(0, geoCap);
	const hiddenGeos = geoChips.slice(geoCap);
	const hidden = [...hiddenGroups, ...hiddenGeos];

	return {
		visible: [...visibleGroups, ...visibleGeos],
		more: hidden.length,
		moreTooltip: hidden.map((c) => c.label).join(" "),
	};
}

function formatCertRowsTooltip(rows: CertificateEntry[]): string {
	if (!rows.length) return "";
	return [...rows]
		.sort(
			(a, b) =>
				a.geo.localeCompare(b.geo) || a.cert_type.localeCompare(b.cert_type),
		)
		.map((c) => `${c.geo} · ${c.cert_type} · ${c.cert_status}`)
		.join("\n");
}

/** @deprecated kept for tests that assert geo lists — prefer getAccountCertDisplay */
export function getAccountCertGeos(certs: CertificateEntry[]): string[] {
	return [
		...new Set(
			certsForColumnDisplay(certs)
				.map((c) => c.geo)
				.filter(Boolean),
		),
	].sort((a, b) => a.localeCompare(b));
}

export function formatAccountCertTooltip(certs: CertificateEntry[]): string {
	const visible = certsForColumnDisplay(certs);
	if (!visible.length) return "";
	return formatCertRowsTooltip(visible);
}

export function accountCertsHavePending(certs: CertificateEntry[]): boolean {
	return certsForColumnDisplay(certs).some((c) =>
		statusPending(c.cert_status),
	);
}
